import Decimal from 'decimal.js'

import type { RfqLabelTranslator } from '@/features/rfq/lib/rfq-format'
import { RfqDraft } from '@/features/rfq/types'
import { OrderSide } from '@/types/generated/common/trading'

/**
 * Client-side mirror of the intent-compiler / venue RFQ rules, so the form gives
 * instant feedback instead of an async refusal. Minimum create amounts are
 * venue-specific and intentionally NOT checked here — the venue rejects those on
 * the stream.
 */
export type RfqValidationCode =
  | 'LEG_COUNT'
  | 'NO_BUY_LEG'
  | 'LEG_SIDE_UNSPECIFIED'
  | 'LEG_SYMBOL_REQUIRED'
  | 'LEG_AMOUNT_INVALID'
  | 'MAKERS_MIN_FIVE'
  | 'HEDGE_SYMBOL_REQUIRED'
  | 'HEDGE_AMOUNT_INVALID'
  | 'HEDGE_SIDE_UNSPECIFIED'
  | 'HEDGE_PRICE_REQUIRED'
  | 'AUTO_THRESHOLD_REQUIRED'
  | 'AUTO_RETRY_INVALID'

export interface RfqValidationError {
  code: RfqValidationCode
  message: string
  /** Dot-path into the draft for field-level highlighting, when applicable. */
  path?: string
}

const MIN_ANONYMOUS_MAKERS = 5
const MAX_LEGS = 20

// Order-form amounts/prices are comma-formatted ("250,000"); strip the grouping commas
// before parsing, exactly as safeParseFloat does, so validation matches the submit gate.
function normalizeDecimal(value: string): string {
  return value.replace(/,/g, '').trim()
}

function isPositiveDecimal(value: string): boolean {
  const v = normalizeDecimal(value)
  if (v === '') return false
  try {
    return new Decimal(v).gt(0)
  } catch {
    return false
  }
}

function isAbsoluteDecimal(value: string): boolean {
  const v = normalizeDecimal(value)
  if (v === '') return false
  try {
    // any finite decimal — Decimal throws on non-numeric input
    new Decimal(v)
    return true
  } catch {
    return false
  }
}

export function validateRfqDraft(draft: RfqDraft, t: RfqLabelTranslator): RfqValidationError[] {
  const errors: RfqValidationError[] = []
  const add = (code: RfqValidationCode, message: string, path?: string) =>
    errors.push({ code, message, path })

  // ── Legs ──────────────────────────────────────────────────────────────────
  if (draft.legs.length < 1 || draft.legs.length > MAX_LEGS) {
    add('LEG_COUNT', t('rfqErrorLegCount', { max: MAX_LEGS }))
  }

  const hasBuyLeg = draft.legs.some((l) => l.side === OrderSide.ORDER_SIDE_BUY)
  if (draft.legs.length > 0 && draft.hedge.enabled && !hasBuyLeg) {
    add('NO_BUY_LEG', t('rfqErrorNoBuyLeg'))
  }

  draft.legs.forEach((leg, i) => {
    if (leg.side === OrderSide.ORDER_SIDE_UNSPECIFIED) {
      add('LEG_SIDE_UNSPECIFIED', t('rfqErrorLegSideRequired'), `legs.${i}.side`)
    }
    if (leg.symbol.trim() === '') {
      add('LEG_SYMBOL_REQUIRED', t('rfqErrorLegSymbolRequired'), `legs.${i}.symbol`)
    }
    if (!isPositiveDecimal(leg.amount)) {
      add('LEG_AMOUNT_INVALID', t('rfqErrorLegAmountInvalid'), `legs.${i}.amount`)
    }
  })

  // ── Maker floor ─────────────────────────────────────────────────────────────
  // Anonymous (disclosed=false) + a non-empty targeted maker list needs >=5.
  // An empty list (all makers) has no floor; disclosed bypasses it entirely.
  const namedMakers = draft.makerIds.filter((m) => m.trim() !== '')
  if (!draft.disclosed && namedMakers.length > 0 && namedMakers.length < MIN_ANONYMOUS_MAKERS) {
    add('MAKERS_MIN_FIVE', t('rfqErrorMakersMinFive', { min: MIN_ANONYMOUS_MAKERS }), 'makerIds')
  }

  // ── Hedge ────────────────────────────────────────────────────────────────────
  if (draft.hedge.enabled) {
    if (draft.hedge.symbol.trim() === '') {
      add('HEDGE_SYMBOL_REQUIRED', t('rfqErrorHedgeSymbolRequired'), 'hedge.symbol')
    }
    if (!isPositiveDecimal(draft.hedge.amount)) {
      add('HEDGE_AMOUNT_INVALID', t('rfqErrorHedgeAmountInvalid'), 'hedge.amount')
    }
    if (draft.hedge.side === OrderSide.ORDER_SIDE_UNSPECIFIED) {
      add('HEDGE_SIDE_UNSPECIFIED', t('rfqErrorHedgeSideRequired'), 'hedge.side')
    }
    if (!isAbsoluteDecimal(draft.hedge.price)) {
      add('HEDGE_PRICE_REQUIRED', t('rfqErrorHedgePriceRequired'), 'hedge.price')
    }
  }

  // ── Auto mode ────────────────────────────────────────────────────────────────
  if (draft.executionMode === 'auto') {
    if (draft.autoApprove.value.trim() === '') {
      add('AUTO_THRESHOLD_REQUIRED', t('rfqErrorAutoThresholdRequired'), 'autoApprove.value')
    }
    if (draft.autoRetry.enabled) {
      const max = draft.autoRetry.maxRetries.trim()
      const until = draft.autoRetry.retryUntilMs.trim()
      const maxInvalid = max !== '' && !(Number.isInteger(Number(max)) && Number(max) >= 1)
      const untilInvalid = until !== '' && !(Number.isInteger(Number(until)) && Number(until) > 0)
      if (maxInvalid || untilInvalid) {
        add('AUTO_RETRY_INVALID', t('rfqErrorAutoRetryInvalid'), 'autoRetry')
      }
    }
  }

  return errors
}
